import { useRef, type MouseEvent, type ReactNode } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion, type Variants } from "framer-motion";
import { Megaphone, BrainCircuit } from "lucide-react";

interface Leader {
  name: string;
  role: string;
  description: string;
  skills: string[];
  icon: ReactNode;
  variant: "creative" | "tech";
}

const leaders: Leader[] = [
  {
    name: "Thainá Ramos",
    role: "Co-fundadora & Head de Marketing e Criação",
    description:
      "Formada em Marketing, especialista em estratégias de conteúdo, branding, comportamento do consumidor e inbound marketing de alta conversão.",
    skills: ["Estratégia de Conteúdo", "Branding", "Comportamento do Consumidor", "Inbound Marketing"],
    icon: <Megaphone size={28} />,
    variant: "creative",
  },
  {
    name: "Yuri Bacil",
    role: "Co-fundador & Head de Inteligência Artificial e Tecnologia",
    description:
      "Cursando Faculdade de Inteligência Artificial, especialista no desenvolvimento de agentes autônomos, integração de dados e arquitetura dos sistemas de PDV e Gestão Financeira.",
    skills: ["Agentes Autônomos", "Integração de Dados", "Arquitetura de Sistemas", "PDV & Gestão Financeira"],
    icon: <BrainCircuit size={28} />,
    variant: "tech",
  },
];

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.2 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const TiltCard = ({ leader }: { leader: Leader }) => {
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();

  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const springRotateX = useSpring(rotateX, { stiffness: 150, damping: 18, mass: 0.5 });
  const springRotateY = useSpring(rotateY, { stiffness: 150, damping: 18, mass: 0.5 });

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    if (reducedMotion || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    rotateY.set(px * 10);
    rotateX.set(-py * 10);
  };

  const handleLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  const isTech = leader.variant === "tech";

  return (
    <motion.div variants={item} style={{ perspective: 1000 }} className="h-full">
      <motion.div
        ref={ref}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{
          rotateX: springRotateX,
          rotateY: springRotateY,
          transformStyle: "preserve-3d",
        }}
        className="group relative h-full rounded-2xl border bg-card/60 backdrop-blur-xl p-8 md:p-10 shadow-lg transition-shadow duration-500 hover:shadow-2xl"
        data-variant={leader.variant}
      >
        {/* Sophisticated border + glow */}
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 rounded-2xl border opacity-100 transition-opacity duration-500 ${
            isTech ? "border-primary/25" : "border-primary/15"
          }`}
        />
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${
            isTech
              ? "from-primary/10 via-transparent to-accent/15"
              : "from-primary/10 via-transparent to-primary/5"
          }`}
        />

        <div className="relative z-10 flex flex-col items-center text-center space-y-6" style={{ transform: "translateZ(30px)" }}>
          <div
            className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-hero-gradient text-primary-foreground shadow-lg shadow-primary/30 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6 ${
              isTech ? "ring-2 ring-primary/20" : ""
            }`}
          >
            {leader.icon}
          </div>

          <div className="space-y-2">
            <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
              {leader.name}
            </h3>
            <p className="text-primary font-medium text-sm md:text-base tracking-wide">
              {leader.role}
            </p>
          </div>

          <p className="text-muted-foreground leading-relaxed text-balance text-sm md:text-base">
            {leader.description}
          </p>

          <div className="flex flex-wrap justify-center gap-2.5 pt-2">
            {leader.skills.map((skill) => (
              <span
                key={skill}
                className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-medium border transition-transform duration-300 hover:scale-105 ${
                  isTech
                    ? "bg-secondary text-secondary-foreground border-primary/30 hover:border-primary/50"
                    : "bg-secondary text-secondary-foreground border-primary/20 hover:border-primary/40"
                }`}
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Leadership = () => {
  return (
    <section id="lideranca" className="relative py-24 md:py-32 bg-section-bg overflow-hidden">
      {/* Decorative gold blobs */}
      <div aria-hidden="true" className="absolute -top-24 -right-24 w-[22rem] h-[22rem] rounded-full bg-primary/10 blur-3xl animate-blob" />
      <div aria-hidden="true" className="absolute -bottom-24 -left-24 w-[26rem] h-[26rem] rounded-full bg-accent/10 blur-3xl animate-blob" style={{ animationDelay: "4s" }} />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center space-y-6 mb-16 md:mb-20"
        >
          <span className="inline-block text-primary font-medium text-sm tracking-widest uppercase">
            Quem lidera
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-foreground text-balance">
            Liderança &{" "}
            <span className="bg-hero-gradient bg-clip-text text-transparent">
              Inteligência Híbrida
            </span>
          </h2>
          <div className="w-20 h-1 bg-hero-gradient mx-auto rounded-full" />
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto text-balance leading-relaxed">
            Na Abillyting, a <span className="text-primary font-medium">inteligência criativa humana</span> —
            marketing, branding e conteúdo — encontra a{" "}
            <span className="text-primary font-medium">engenharia de IA</span>: agentes autônomos,
            automações e arquitetura de sistemas. O resultado é uma estratégia híbrida que escala
            negócios com velocidade, precisão e propósito.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10"
        >
          {leaders.map((leader) => (
            <TiltCard key={leader.name} leader={leader} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Leadership;
